import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  UserGroupIcon,
  SparklesIcon,
  DocumentTextIcon,
  TrashIcon,
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  QuestionMarkCircleIcon,
  StarIcon,
  TagIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import {
  getTalentPool,
  summarizeFeedback,
  summarizeFeedbackPreview,
  getFeedbackHistory,
  getFeedbackAnalytics,
  deleteFeedback
} from '../services/api';

const COLORS = ['#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e', '#f97316', '#eab308'];
const RECOMMENDATION_COLORS = {
  yes: '#22c55e',
  no: '#ef4444',
  maybe: '#eab308'
};

function Feedback() {
  // Candidate selection state
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [loadingCandidates, setLoadingCandidates] = useState(false);

  // Feedback input state
  const [interviewerNotes, setInterviewerNotes] = useState('');
  const [interviewStage, setInterviewStage] = useState('');
  const [interviewerName, setInterviewerName] = useState('');

  // AI Summary state
  const [previewResult, setPreviewResult] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [savingFeedback, setSavingFeedback] = useState(false);

  // Feedback history and analytics state
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  // Interview stage options
  const interviewStages = [
    'Phone Screen',
    'Technical Round 1',
    'Technical Round 2',
    'System Design',
    'Behavioral',
    'Culture Fit',
    'Final Round',
    'Hiring Manager',
    'Other'
  ];

  // Fetch candidates on mount
  useEffect(() => {
    fetchCandidates();
  }, []);

  // Fetch feedback history and analytics when candidate changes
  useEffect(() => {
    if (selectedCandidate) {
      fetchFeedbackHistory(selectedCandidate.id);
      fetchAnalytics(selectedCandidate.id);
    } else {
      setFeedbackHistory([]);
      setAnalytics(null);
    }
  }, [selectedCandidate]);

  const fetchCandidates = async () => {
    setLoadingCandidates(true);
    try {
      const response = await getTalentPool();
      setCandidates(response.data.candidates || []);
    } catch (error) {
      console.error('Error fetching candidates:', error);
      toast.error('Failed to load candidates');
    } finally {
      setLoadingCandidates(false);
    }
  };

  const fetchFeedbackHistory = async (candidateId) => {
    setLoadingHistory(true);
    try {
      const response = await getFeedbackHistory(candidateId);
      setFeedbackHistory(response.data.feedbackHistory || []);
    } catch (error) {
      console.error('Error fetching feedback history:', error);
      // Don't show error toast - candidate might have no feedback yet
      setFeedbackHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchAnalytics = async (candidateId) => {
    setLoadingAnalytics(true);
    try {
      const response = await getFeedbackAnalytics(candidateId);
      setAnalytics(response.data.analytics);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setAnalytics(null);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const handleGeneratePreview = async () => {
    if (!interviewerNotes.trim()) {
      toast.error('Please enter interview notes');
      return;
    }

    setLoadingPreview(true);
    setPreviewResult(null);

    try {
      const response = await summarizeFeedbackPreview({
        interviewerNotes,
        interviewStage: interviewStage || undefined,
        candidateName: selectedCandidate?.fullName,
        role: selectedCandidate?.desiredRoles
      });
      setPreviewResult(response.data);
      toast.success('AI summary generated');
    } catch (error) {
      console.error('Error generating preview:', error);
      toast.error(error.response?.data?.error || 'Failed to generate summary');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSaveFeedback = async () => {
    if (!selectedCandidate) {
      toast.error('Please select a candidate');
      return;
    }

    if (!interviewerNotes.trim()) {
      toast.error('Please enter interview notes');
      return;
    }

    setSavingFeedback(true);

    try {
      const response = await summarizeFeedback({
        candidateId: selectedCandidate.id,
        interviewerNotes,
        interviewStage: interviewStage || undefined,
        interviewerName: interviewerName || undefined,
        saveToCandidate: true
      });

      toast.success('Feedback saved successfully');

      // Update preview with saved result
      setPreviewResult(response.data);

      // Refresh history and analytics
      fetchFeedbackHistory(selectedCandidate.id);
      fetchAnalytics(selectedCandidate.id);

      // Clear form
      setInterviewerNotes('');
      setInterviewStage('');
      setInterviewerName('');
    } catch (error) {
      console.error('Error saving feedback:', error);
      toast.error(error.response?.data?.error || 'Failed to save feedback');
    } finally {
      setSavingFeedback(false);
    }
  };

  const handleDeleteFeedback = async (feedbackId) => {
    if (!selectedCandidate) return;

    if (!window.confirm('Are you sure you want to delete this feedback?')) {
      return;
    }

    try {
      await deleteFeedback(selectedCandidate.id, feedbackId);
      toast.success('Feedback deleted');

      // Refresh data
      fetchFeedbackHistory(selectedCandidate.id);
      fetchAnalytics(selectedCandidate.id);
    } catch (error) {
      console.error('Error deleting feedback:', error);
      toast.error(error.response?.data?.error || 'Failed to delete feedback');
    }
  };

  const getRecommendationIcon = (recommendation) => {
    switch (recommendation) {
      case 'yes':
        return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
      case 'no':
        return <XCircleIcon className="h-5 w-5 text-red-600" />;
      case 'maybe':
        return <QuestionMarkCircleIcon className="h-5 w-5 text-yellow-600" />;
      default:
        return null;
    }
  };

  const getRecommendationBadge = (recommendation) => {
    const colors = {
      yes: 'bg-green-100 text-green-800 border-green-300',
      no: 'bg-red-100 text-red-800 border-red-300',
      maybe: 'bg-yellow-100 text-yellow-800 border-yellow-300'
    };
    return colors[recommendation] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          star <= rating ? (
            <StarIconSolid key={star} className="h-4 w-4 text-yellow-500" />
          ) : (
            <StarIcon key={star} className="h-4 w-4 text-gray-300" />
          )
        ))}
      </div>
    );
  };

  // Prepare chart data for analytics
  const getRecommendationChartData = () => {
    if (!analytics?.recommendationDistribution) return [];
    return Object.entries(analytics.recommendationDistribution).map(([key, value]) => ({
      name: key.charAt(0).toUpperCase() + key.slice(1),
      value,
      color: RECOMMENDATION_COLORS[key] || '#9ca3af'
    }));
  };

  const getTagsChartData = () => {
    if (!analytics?.topTags) return [];
    return analytics.topTags.slice(0, 6).map((item, index) => ({
      name: item.tag.length > 12 ? item.tag.substring(0, 12) + '...' : item.tag,
      count: item.count,
      fill: COLORS[index % COLORS.length]
    }));
  };

  const getRatingChartData = () => {
    if (!analytics?.ratingDistribution) return [];
    return Object.entries(analytics.ratingDistribution)
      .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
      .map(([rating, count]) => ({
        name: `${rating} Star${rating !== '1' ? 's' : ''}`,
        count,
        fill: COLORS[parseInt(rating) - 1]
      }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI Feedback Summarizer</h1>
          <p className="mt-1 text-sm text-gray-500">
            Summarize interview notes into structured feedback using AI
          </p>
        </div>
        <div className="flex items-center gap-2">
          <SparklesIcon className="h-8 w-8 text-indigo-600" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Input Section */}
        <div className="lg:col-span-2 space-y-6">
          {/* Step 1: Select Candidate */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 font-semibold text-sm">
                1
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Select Candidate</h2>
            </div>

            <div className="flex items-center gap-4">
              <UserGroupIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
              <select
                className="flex-1 rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                value={selectedCandidate?.id || ''}
                onChange={(e) => {
                  const candidate = candidates.find(c => c.id === e.target.value);
                  setSelectedCandidate(candidate || null);
                  setPreviewResult(null);
                }}
                disabled={loadingCandidates}
              >
                <option value="">
                  {loadingCandidates ? 'Loading candidates...' : 'Choose a candidate from the talent pool'}
                </option>
                {candidates.map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>
                    {candidate.fullName} - {candidate.email}
                    {candidate.desiredRoles ? ` (${candidate.desiredRoles})` : ''}
                  </option>
                ))}
              </select>
              <button
                onClick={fetchCandidates}
                className="p-2 text-gray-500 hover:text-indigo-600 transition-colors"
                title="Refresh candidates"
              >
                <ArrowPathIcon className={`h-5 w-5 ${loadingCandidates ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {selectedCandidate && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                      <span className="text-indigo-600 font-semibold text-lg">
                        {selectedCandidate.fullName?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{selectedCandidate.fullName}</p>
                    <p className="text-sm text-gray-500">{selectedCandidate.email}</p>
                    {selectedCandidate.desiredRoles && (
                      <p className="text-sm text-indigo-600 mt-1">{selectedCandidate.desiredRoles}</p>
                    )}
                    {selectedCandidate.experienceLevel && (
                      <span className="inline-flex items-center mt-2 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {selectedCandidate.experienceLevel}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Step 2: Enter Notes */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 font-semibold text-sm">
                2
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Enter Interview Notes</h2>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Interview Stage
                  </label>
                  <select
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    value={interviewStage}
                    onChange={(e) => setInterviewStage(e.target.value)}
                  >
                    <option value="">Select stage (optional)</option>
                    {interviewStages.map((stage) => (
                      <option key={stage} value={stage}>{stage}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Interviewer Name
                  </label>
                  <input
                    type="text"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Your name (optional)"
                    value={interviewerName}
                    onChange={(e) => setInterviewerName(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Interview Notes <span className="text-red-500">*</span>
                </label>
                <textarea
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 min-h-[200px]"
                  placeholder="Paste your raw interview notes here. Include observations about technical skills, communication, problem-solving, cultural fit, etc."
                  value={interviewerNotes}
                  onChange={(e) => setInterviewerNotes(e.target.value)}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {interviewerNotes.length} characters
                </p>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={handleGeneratePreview}
                  disabled={loadingPreview || !interviewerNotes.trim()}
                  className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loadingPreview ? (
                    <>
                      <ArrowPathIcon className="h-5 w-5 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <SparklesIcon className="h-5 w-5" />
                      Generate AI Summary
                    </>
                  )}
                </button>

                <button
                  onClick={handleSaveFeedback}
                  disabled={savingFeedback || !selectedCandidate || !interviewerNotes.trim()}
                  className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {savingFeedback ? (
                    <>
                      <ArrowPathIcon className="h-5 w-5 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="h-5 w-5" />
                      Save Feedback
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Step 3: AI Summary Preview */}
          {previewResult && (
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-600 font-semibold text-sm">
                  <CheckCircleIcon className="h-5 w-5" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">AI Summary</h2>
                {previewResult.preview && (
                  <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                    Preview - Not Saved
                  </span>
                )}
                {previewResult.saved && (
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                    Saved
                  </span>
                )}
              </div>

              <div className="space-y-4">
                {/* Summary */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Summary</h3>
                  <p className="text-gray-900 bg-gray-50 rounded-lg p-4">
                    {previewResult.summary}
                  </p>
                </div>

                {/* Rating & Recommendation */}
                <div className="flex items-center gap-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Rating</h3>
                    <div className="flex items-center gap-2">
                      {renderStars(previewResult.rating)}
                      <span className="text-sm text-gray-600">({previewResult.rating}/5)</span>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Recommendation</h3>
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border ${getRecommendationBadge(previewResult.recommendation)}`}>
                      {getRecommendationIcon(previewResult.recommendation)}
                      {previewResult.recommendation?.charAt(0).toUpperCase() + previewResult.recommendation?.slice(1)}
                    </span>
                  </div>
                </div>

                {/* Tags */}
                {previewResult.tags && previewResult.tags.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Tags</h3>
                    <div className="flex flex-wrap gap-2">
                      {previewResult.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-indigo-50 text-indigo-700 border border-indigo-200"
                        >
                          <TagIcon className="h-3.5 w-3.5" />
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Feedback History */}
          {selectedCandidate && (
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <ClockIcon className="h-6 w-6 text-gray-400" />
                  <h2 className="text-lg font-semibold text-gray-900">Feedback History</h2>
                  <span className="text-sm text-gray-500">
                    ({feedbackHistory.length} entries)
                  </span>
                </div>
                <button
                  onClick={() => fetchFeedbackHistory(selectedCandidate.id)}
                  className="p-2 text-gray-500 hover:text-indigo-600 transition-colors"
                  title="Refresh history"
                >
                  <ArrowPathIcon className={`h-5 w-5 ${loadingHistory ? 'animate-spin' : ''}`} />
                </button>
              </div>

              {loadingHistory ? (
                <div className="flex items-center justify-center py-8">
                  <ArrowPathIcon className="h-8 w-8 text-indigo-600 animate-spin" />
                </div>
              ) : feedbackHistory.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <DocumentTextIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No feedback recorded yet for this candidate.</p>
                </div>
              ) : (
                <div className="space-y-4 max-h-[500px] overflow-y-auto">
                  {feedbackHistory.slice().reverse().map((feedback) => (
                    <div
                      key={feedback.id}
                      className="border border-gray-200 rounded-lg p-4 hover:border-indigo-200 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          {feedback.interviewStage && (
                            <span className="text-sm font-medium text-indigo-600 bg-indigo-50 px-2 py-1 rounded">
                              {feedback.interviewStage}
                            </span>
                          )}
                          {feedback.interviewerName && (
                            <span className="text-sm text-gray-500">
                              by {feedback.interviewerName}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-400">
                            {new Date(feedback.createdAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                          <button
                            onClick={() => handleDeleteFeedback(feedback.id)}
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                            title="Delete feedback"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      <p className="text-sm text-gray-700 mb-3">{feedback.summary}</p>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {renderStars(feedback.rating)}
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${getRecommendationBadge(feedback.recommendation)}`}>
                            {getRecommendationIcon(feedback.recommendation)}
                            {feedback.recommendation}
                          </span>
                        </div>

                        {feedback.tags && feedback.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {feedback.tags.slice(0, 3).map((tag, index) => (
                              <span
                                key={index}
                                className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded"
                              >
                                {tag}
                              </span>
                            ))}
                            {feedback.tags.length > 3 && (
                              <span className="text-xs text-gray-400">
                                +{feedback.tags.length - 3} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Column - Analytics Panel */}
        <div className="space-y-6">
          {/* Analytics Cards */}
          {selectedCandidate && (
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <ChartBarIcon className="h-6 w-6 text-indigo-600" />
                <h2 className="text-lg font-semibold text-gray-900">Analytics</h2>
              </div>

              {loadingAnalytics ? (
                <div className="flex items-center justify-center py-8">
                  <ArrowPathIcon className="h-8 w-8 text-indigo-600 animate-spin" />
                </div>
              ) : !analytics || analytics.totalFeedback === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <ChartBarIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No analytics available yet.</p>
                  <p className="text-sm">Add feedback to see insights.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-indigo-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-indigo-600">
                        {feedbackHistory.length}
                      </p>
                      <p className="text-xs text-gray-600">Total Feedback</p>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-yellow-600">
                        {analytics.averageRating?.toFixed(1) || 'N/A'}
                      </p>
                      <p className="text-xs text-gray-600">Avg Rating</p>
                    </div>
                  </div>

                  {/* Recommendation Distribution */}
                  {getRecommendationChartData().length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-3">
                        Recommendation Distribution
                      </h3>
                      <ResponsiveContainer width="100%" height={180}>
                        <PieChart>
                          <Pie
                            data={getRecommendationChartData()}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={70}
                            paddingAngle={2}
                            dataKey="value"
                          >
                            {getRecommendationChartData().map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend
                            layout="horizontal"
                            verticalAlign="bottom"
                            align="center"
                            wrapperStyle={{ fontSize: '12px' }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Rating Distribution */}
                  {getRatingChartData().length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-3">
                        Rating Distribution
                      </h3>
                      <ResponsiveContainer width="100%" height={150}>
                        <BarChart data={getRatingChartData()} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                          <XAxis type="number" allowDecimals={false} fontSize={12} />
                          <YAxis type="category" dataKey="name" width={60} fontSize={11} />
                          <Tooltip />
                          <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                            {getRatingChartData().map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Top Tags */}
                  {getTagsChartData().length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-3">
                        Top Tags
                      </h3>
                      <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={getTagsChartData()}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis dataKey="name" fontSize={10} angle={-45} textAnchor="end" height={60} />
                          <YAxis allowDecimals={false} fontSize={12} />
                          <Tooltip />
                          <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                            {getTagsChartData().map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Interview Stages Covered */}
                  {analytics.interviewStagesCovered && analytics.interviewStagesCovered.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-2">
                        Stages Completed
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {analytics.interviewStagesCovered.map((stage, index) => (
                          <span
                            key={index}
                            className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full"
                          >
                            {stage}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Quick Tips */}
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg border border-indigo-100 p-6">
            <h3 className="text-sm font-semibold text-indigo-900 mb-3">
              Tips for Better Feedback
            </h3>
            <ul className="space-y-2 text-sm text-indigo-800">
              <li className="flex items-start gap-2">
                <CheckCircleIcon className="h-4 w-4 mt-0.5 text-indigo-600 flex-shrink-0" />
                Be specific with examples
              </li>
              <li className="flex items-start gap-2">
                <CheckCircleIcon className="h-4 w-4 mt-0.5 text-indigo-600 flex-shrink-0" />
                Include both strengths and areas for improvement
              </li>
              <li className="flex items-start gap-2">
                <CheckCircleIcon className="h-4 w-4 mt-0.5 text-indigo-600 flex-shrink-0" />
                Note communication and cultural fit
              </li>
              <li className="flex items-start gap-2">
                <CheckCircleIcon className="h-4 w-4 mt-0.5 text-indigo-600 flex-shrink-0" />
                Mention relevant technical skills
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Feedback;
